// src/components/StoreSelector/index.js
// ✅ FIXED: Proper location filtering and distance calculation
import React, { useState, useEffect } from 'react';
import { X, MapPin, Clock, Search, Navigation, ShoppingBag, AlertCircle, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { calculateDistance, getShortAddress } from '../../utils/storeUtils';
import { getDrivingDistance } from '../../utils/routing'; // ✅ Import new routing utility
import { checkStoreAvailability } from '../checkStoreAvailability';
import { getTenantId } from '../../config/tenantConfig';
import { apiCache } from '../../utils/apiCache';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const StoreSelector = ({
  isOpen,
  onClose,
  onSelectStore,
  userLocation,
  onRequestLocation,
  isHidden = false,
  outletType = 'sweet_shop' // Default to sweet-shop logic (bypass distance check)
}) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('distance');

  // Pincode search states
  const [pincode, setPincode] = useState('');
  const [pincodeLocation, setPincodeLocation] = useState(null);
  const [pincodeResolvedName, setPincodeResolvedName] = useState(''); // ✅ NEW: Store resolved location name
  const [pincodeError, setPincodeError] = useState('');
  const [searchingPincode, setSearchingPincode] = useState(false);
  const [showDistantStores, setShowDistantStores] = useState(false);

  // ✅ CRITICAL DEBUG: Log what location is being received
  useEffect(() => {
    console.log('🔍 CRITICAL DEBUG - StoreSelector Props:', {
      isOpen,
      userLocation: userLocation,
      userLocationStringified: JSON.stringify(userLocation),
      hasPincodeLocation: !!pincodeLocation,
      outletType // Debugging outlet type
    });

    if (userLocation) {
      console.log('📍 User Location Details:', {
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        isVijayawada: userLocation.latitude >= 16 && userLocation.latitude <= 17 && userLocation.longitude >= 80 && userLocation.longitude <= 81,
        isHyderabad: userLocation.latitude >= 17 && userLocation.latitude <= 18 && userLocation.longitude >= 78 && userLocation.longitude <= 79
      });
    }
  }, [isOpen, userLocation, pincodeLocation, outletType]);

  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Api-Key': X_API_KEY,
      'X-Tenant': getTenantId()
    }
  });

  // ✅ Helper: Check if outlet type is a sweet shop (bypasses radius restrictions)
  const isSweetShop = outletType === 'sweet_shop';

  // Helper: Convert outlet_type slug to readable label for display
  const getOutletLabel = (type) => {
    const labels = {
      sweet_shop: 'Sweet Shop',
      restaurant: 'Restaurant',
      meat_shop: 'Meat Shop',
      meat_store: 'Meat Store',
      bakery: 'Bakery',
      cafe: 'Café',
      grocery: 'Grocery',
      cloud_kitchen: 'Cloud Kitchen',
    };
    return labels[type] || (type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '');
  };

  // ✅ SHARED: Process stores with location — calculates distance, sorts, filters by radius
  // Used by both fetchStores() and handlePincodeSearch() to avoid duplicated logic
  const processStoresWithLocation = (allStores, locationData) => {
    // Calculate haversine distance (cheap math — always useful for sorting)
    const storesWithDistance = allStores.map(store => ({
      ...store,
      distance: calculateDistance(
        locationData.latitude,
        locationData.longitude,
        parseFloat(store.latitude),
        parseFloat(store.longitude)
      )
    }));

    // Sort by distance
    const sortedStores = storesWithDistance.sort((a, b) => {
      if (a.distance === undefined || isNaN(a.distance)) return 1;
      if (b.distance === undefined || isNaN(b.distance)) return -1;
      return a.distance - b.distance;
    });

    // ✅ Sweet shops: Show ALL stores (nationwide delivery), no radius filter
    // ✅ Restaurants: Mark out-of-range stores but still show them (for collection/pickup)
    const processedStores = sortedStores.map(store => {
      if (isSweetShop) return { ...store, outOfRange: false };

      const radius = store.delivery_radius || 50;
      const outOfRange = !store.distance || store.distance > radius;
      return { ...store, outOfRange };
    });

    // ✅ Sort: in-range stores first, then out-of-range stores (both sorted by distance)
    processedStores.sort((a, b) => {
      if (a.outOfRange !== b.outOfRange) return a.outOfRange ? 1 : -1;
      if (a.distance === undefined || isNaN(a.distance)) return 1;
      if (b.distance === undefined || isNaN(b.distance)) return -1;
      return a.distance - b.distance;
    });

    const inRange = processedStores.filter(s => !s.outOfRange).length;
    console.log(`📍 Showing ${processedStores.length} stores (${inRange} in range, ${processedStores.length - inRange} out of range) [${isSweetShop ? 'sweet-shop' : 'restaurant'}]`);
    return processedStores;
  };

  // ✅ OPTIMIZED: Fetch driving distances only for restaurants (sweet shops don't need it)
  const fetchAccurateDistances = async (allFilteredStores, originLat, originLon) => {
    // Skip driving distance API for sweet shops — haversine is sufficient for display
    if (isSweetShop) {
      console.log('🍬 Sweet shop: Skipping driving distance API (haversine is enough for display)');
      return;
    }

    // Only fetch for nearby stores (within 50km straight-line)
    const nearbyCandidates = allFilteredStores.filter(
      s => s.distance && !isNaN(s.distance) && s.distance <= 50
    );
    if (nearbyCandidates.length === 0) return;

    console.log(`🚗 Fetching driving distances for ${nearbyCandidates.length} nearby restaurants...`);

    // Build a map of store ID → driving distance
    const drivingDistanceMap = new Map();

    await Promise.all(nearbyCandidates.map(async (store) => {
      try {
        const driving = await getDrivingDistance(
          originLat, originLon,
          parseFloat(store.latitude), parseFloat(store.longitude)
        );
        if (driving && driving.distanceValue) {
          drivingDistanceMap.set(store.id, driving.distanceValue / 1000);
        }
      } catch (err) {
        console.warn(`⚠️ Failed to get driving distance for ${store.name}:`, err);
      }
    }));

    // ✅ Merge driving distances into the FULL filtered list (not just nearby subset)
    const updatedStores = allFilteredStores.map(store => {
      const drivingDist = drivingDistanceMap.get(store.id);
      return drivingDist !== undefined ? { ...store, distance: drivingDist } : store;
    });

    // Re-sort after updating distances
    updatedStores.sort((a, b) => {
      if (a.distance === undefined || isNaN(a.distance)) return 1;
      if (b.distance === undefined || isNaN(b.distance)) return -1;
      return a.distance - b.distance;
    });

    setStores(updatedStores);
  };

  const fetchStores = async () => {
    try {
      setLoading(true);
      setPincodeError('');
      console.log('Fetching stores from API StoreSelector');

      // 1. Fetch fresh branches from API to ensure live schedule availability
      let allBranches;
      try {
        const response = await apiClient.get('/web/branch', { params: { _t: Date.now() } });
        if (response.data && response.data.data) {
          allBranches = response.data.data;
          apiCache.setBranches(allBranches);
        } else {
          allBranches = apiCache.getBranches() || [];
        }
      } catch (e) {
        allBranches = apiCache.getBranches() || [];
      }

      console.log(`📍 Found ${allBranches.length} total stores`);

      // 2. Enrich with cached details
      const storesWithDetails = allBranches.map(branch => {
        const cached = apiCache.getStoreDetails(branch.id);
        return {
          ...branch,
          location: branch.location || `${branch.city}, ${branch.state}`,
          week_schedule: cached?.week_schedule || branch.week_schedule,
          allow_pre_order: cached?.allow_pre_order || branch.allow_pre_order || false,
          pre_order_enabled: cached?.pre_order_enabled || branch.pre_order_enabled || false
        };
      });

      // 3. Process with location if available
      const locationToUse = pincodeLocation || userLocation;

      if (locationToUse && locationToUse.latitude && locationToUse.longitude) {
        const filteredStores = processStoresWithLocation(storesWithDetails, locationToUse);
        setStores(filteredStores);

        if (filteredStores.length > 0) setPincodeError('');

        // 🚀 Background: Fetch accurate driving distances (only for restaurants)
        // fetchAccurateDistances(filteredStores, locationToUse.latitude, locationToUse.longitude);
      } else {
        console.log('⚠️ No location available, showing all stores');
        setStores(storesWithDetails);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStores();
    }
  }, [isOpen, userLocation, pincodeLocation, outletType]);

  const handlePincodeSearch = async (e) => {
    if (e) e.preventDefault();
    if (pincode.length !== 6) {
      setPincodeError('Please enter a valid 6-digit pincode.');
      return;
    }

    setSearchingPincode(true);
    setPincodeError('');

    try {
      console.log(`🔍 Searching for location for pincode: ${pincode}`);
      const res = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);

      if (res.data[0].Status === 'Success' && res.data[0].PostOffice?.length > 0) {
        const postOffice = res.data[0].PostOffice[0];
        const locationName = `${postOffice.Name}, ${postOffice.District}`;
        console.log(`✅ Pincode found: ${locationName}`);

        // Get lat/long from District/State
        const geocodeRes = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(postOffice.District + ', ' + postOffice.State + ', India')}&limit=1`);

        const location = geocodeRes.data.length > 0
          ? { latitude: parseFloat(geocodeRes.data[0].lat), longitude: parseFloat(geocodeRes.data[0].lon) }
          : { latitude: 17.3850, longitude: 78.4867 };

        setPincodeLocation(location);
        setPincodeResolvedName(locationName);

        // Fetch branches (from cache or API)
        let allStores = apiCache.getBranches();
        if (!allStores) {
          const branchesResponse = await apiClient.get('/web/branch');
          allStores = branchesResponse.data?.data || [];
          if (allStores.length) apiCache.setBranches(allStores);
        }

        // ✅ Use shared processing logic (no duplication)
        const filteredStores = processStoresWithLocation(allStores, location);
        setStores(filteredStores);
        setPincodeError('');
        setShowDistantStores(false);

        // 🚀 Background: Fetch accurate driving distances (only for restaurants)
        // fetchAccurateDistances(filteredStores, location.latitude, location.longitude);
      } else {
        setPincodeError('Unable to find location for this pincode. Please try a different pincode.');
      }
    } catch (err) {
      console.error('❌ Pincode search error:', err);
      setPincodeError('Error searching for pincode. Please try again.');
    } finally {
      setSearchingPincode(false);
    }
  };

  const clearPincodeSearch = () => {
    setPincode('');
    setPincodeLocation(null);
    setPincodeResolvedName('');
    setShowDistantStores(false);
    setPincodeError('');

    // Refresh with userLocation
    fetchStores();
  };

  const handleManualStoreSelect = (store) => {
    console.log('✅ Store manually selected:', store.name);
    const directOrderType = store.outOfRange ? 'collection' : null;
    onSelectStore(store, directOrderType);
  };

  if (!isOpen) return null;

  return (
    <div className={`store-selector-overlay ${isHidden ? 'hidden' : ''}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="store-selector-container">
        <div className="store-selector-header">
          <div className="header-top">
            <h2 className="store-selector-title">Select a Store</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <p className="store-selector-subtitle">Choose your preferred location</p>

          <div className="search-controls">
            <div className="search-input-wrapper">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search Stores"
                className="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <form onSubmit={handlePincodeSearch} className="pincode-input-wrapper">
              <MapPin className="pincode-icon" size={18} />
              <input
                type="text"
                maxLength="6"
                placeholder="Enter Postcode"
                className={`pincode-input ${pincodeError ? 'error' : ''}`}
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
              />
              {pincodeLocation && (
                <button type="button" className="clear-pincode" onClick={clearPincodeSearch}>
                  <X size={14} />
                </button>
              )}
              {pincode.length === 6 && !pincodeLocation && (
                <button type="submit" className="search-go-btn" disabled={searchingPincode}>
                  {searchingPincode ? '...' : 'Go'}
                </button>
              )}
            </form>
          </div>

          {pincodeError && <p className="pincode-error-msg">{pincodeError}</p>}

          {pincodeResolvedName && (
            <div className="resolved-location-badge">
              <Navigation size={14} />
              <span>Showing stores near <strong>{pincodeResolvedName}</strong></span>
            </div>
          )}

          <div className="sort-controls">
            <button
              className={`sort-btn ${sortBy === 'distance' ? 'active' : ''}`}
              onClick={() => setSortBy('distance')}
            >
              Nearest First
            </button>
            <button
              className={`sort-btn ${sortBy === 'name' ? 'active' : ''}`}
              onClick={() => setSortBy('name')}
            >
              Alphabetical
            </button>
          </div>
        </div>

        <div className="store-selector-content custom-scrollbar">
          {loading ? (
            <div className="store-selector-loading">
              <div className="spinner"></div>
              <p>Finding stores near you...</p>
            </div>
          ) : stores.length > 0 ? (
            <div className="stores-grid">
              {stores
                .filter(store =>
                  store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (store.location && store.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
                  (store.address && store.address.toLowerCase().includes(searchQuery.toLowerCase()))
                )
                .sort((a, b) => {
                  if (sortBy === 'distance') {
                    if (a.distance === undefined || isNaN(a.distance)) return 1;
                    if (b.distance === undefined || isNaN(b.distance)) return -1;
                    return a.distance - b.distance;
                  }
                  return a.name.localeCompare(b.name);
                })
                .map(store => {
                  const availability = checkStoreAvailability(store.week_schedule);

                  return (
                    <div
                      key={store.id}
                      className={`store-card ${!availability.isOpen ? 'closed' : ''} ${store.outOfRange ? 'out-of-range' : ''}`}
                      onClick={() => handleManualStoreSelect(store)}
                    >
                      <div className="store-card-header">
                        <div className="store-card-title-row">
                          <ShoppingBag size={16} className="store-card-title-icon" />
                          <h3 className="store-card-name">{store.name}</h3>
                        </div>
                        <div className={`store-card-badge ${store.outOfRange ? 'out-of-range' : ''}`}>
                          {store.outOfRange ? 'Out of Range' : (availability.isOpen ? 'Open' : 'Closed')}
                        </div>
                      </div>

                      <div className="store-card-body">
                        {/* Category tag
                        {outletType && (
                          <span className="store-card-category">{getOutletLabel(outletType)}</span>
                        )} */}

                        {/* Concise address */}
                        <div className="store-card-info-row">
                          <MapPin size={13} className="info-icon" />
                          <span className="info-value">{getShortAddress(store)}</span>
                        </div>

                        {/* Status */}
                        <div className="store-card-info-row">
                          <Clock size={13} className="info-icon" />
                          <span className={`info-value ${availability.isOpen ? 'open-text' : 'closed-text'}`}>
                            {availability.message}
                          </span>
                        </div>

                        {store.outOfRange && (
                          <div className="out-of-range-notice">
                            <AlertCircle size={12} />
                            <span>Collection/pickup only</span>
                          </div>
                        )}
                      </div>

                      <div className="store-card-footer">
                        <button className="select-store-btn">
                          {store.outOfRange
                            ? 'Order for Collection'
                            : (availability.isOpen ? 'Order from here' : 'View Schedule')
                          }
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="store-selector-empty">
              <MapPin size={48} className="empty-icon" />
              <p className="empty-title">
                {pincodeError ? 'Service Unavailable Here' : 'No stores found'}
              </p>
              <p className="empty-text">
                {pincodeError
                  ? pincodeError
                  : (pincode ? 'Try a different pincode or clear the search' : 'No stores available at the moment.')
                }
              </p>
              {pincode && (
                <button className="clear-search-btn" onClick={clearPincodeSearch}>
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>

        {(!userLocation || (!userLocation.latitude && !pincodeLocation)) && !loading && (
          <div className="store-selector-footer-tip">
            <AlertCircle size={14} />
            <p>Enable location permissions or enter a pincode for precise distances.</p>
            <button className="detect-location-btn" onClick={onRequestLocation}>Detect My Location</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreSelector;